import { DatePipe } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  addDays,
  addYears,
  differenceInDays,
  format,
  getWeek,
  isAfter,
  isBefore,
  subDays,
  subYears,
} from 'date-fns';
import { Mongo } from 'meteor/mongo';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Jobs } from 'server/shared/collections/jobs';
import { Organisations } from 'server/shared/collections/organisations';
import { AggregatedJobMatrixJob } from 'server/shared/models/aggregatedJobMatrixJobs';
import { CertificateGroup } from 'server/shared/models/certificateGroup';
import { Customer } from 'server/shared/models/customer';
import { Job } from 'server/shared/models/job';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { Organisation } from 'server/shared/models/organisation';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import {
  DispoUser,
  MappedCertificate,
  RequesterUser,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxDelayLoadingState } from 'src/helpers/rxDelayLoadingState';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CertificateService } from '../services/certificate.service';
import { CustomerService } from '../services/customer.service';
import { ToastService } from '../services/toast/toast.service';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';
import { AggregatedJobMatrixJobs } from '../shared/collections/aggregatedJobMatrixJobs';
import { SubscriptionService } from '../shared/subscription.service';

// Add calendar helper interfaces
interface CalendarDay {
  date: Date;
  totalIndex: number;
}

interface CalendarMonth {
  name: string;
  days: CalendarDay[];
}

interface CalendarWeek {
  weekNumber: string;
  days: CalendarDay[];
}

interface CalendarTableHelper {
  start: Date;
  end: Date;
  days: Date[];
  months: CalendarMonth[];
  weeks: CalendarWeek[];
}

type JobMatrixJobsByCertificates = {
  byDate: Map<
    string,
    {
      byShift: Map<string, { open: number; closed: number; jobIds: string[] }>;
    }
  >;
};

type JobMatrixJobs = {
  maxJobs: number;
  byCertificates: Map<string, JobMatrixJobsByCertificates>;
};

type RequestOverviewDepartment = {
  id: string;
  name: string;
  jobs: JobMatrixJobs;
};

type RequestOverviewSection = {
  detailsCollapsed: boolean;
  name: string;
  jobs: JobMatrixJobs;
  departments: RequestOverviewDepartment[];
};
interface RequestOverviewOrganisationRowBase {
  detailsCollapsed: boolean;
  organisation: {
    id: string;
    name: string;
  };
  jobs: JobMatrixJobs;
}

interface RequestOverviewOrganisationRowWithSections
  extends RequestOverviewOrganisationRowBase {
  sections: RequestOverviewSection[];
  departments?: RequestOverviewDepartment[];
}

interface RequestOverviewOrganisationRowWithoutSections
  extends RequestOverviewOrganisationRowBase {
  departments: RequestOverviewDepartment[];
}

type RequestOverviewOrganisationRow =
  | RequestOverviewOrganisationRowWithSections
  | RequestOverviewOrganisationRowWithoutSections;

interface RequestOverviewOrganisationRowForTemplate
  extends Omit<RequestOverviewOrganisationRow, 'sections' | 'departments'> {
  sections?: RequestOverviewSection[];
  departments?: RequestOverviewDepartment[];
}

type SectionDepartmentGroup = {
  sectionName: string;
  organisationId: string;
  organisationName: string;
  departmentId: string;
  departmentName: string;
  jobs: JobMatrixJobs;
};

@Component({
  selector: 'job-matrix',
  templateUrl: 'job-matrix.component.html',
  styleUrls: ['job-matrix.component.scss', 'job-matrix-custom-table.scss'],
})
export class JobMatrixComponent implements OnInit, OnDestroy {
  i18n = this.translationService.getTranslations('job-matrix');
  roleEnum = RoleEnum;
  loggedInUser?: DispoUser | RequesterUser;
  destroyed$ = new Subject<void>();

  readyDispoUser$ = this.userService.readyUser$ as Observable<DispoUser>;

  // Track collapsed state for organizations and sections
  collapsedOrganisations = new Map<string, boolean>();
  collapsedSections = new Map<string, boolean>();

  // Side sheet state for job details
  sideSheetOpen = false;
  private selectedJobIdsSubject$ = new Subject<string[]>();
  sideSheetTitle = '';

  // Filter accordion state
  filtersCollapsed =
    localStorage.getItem('jobMatrixFiltersCollapsed') === 'true';

  filterCounts = 0;

  groupByOrganisation$ = new BehaviorSubject<boolean>(
    localStorage.getItem('jobMatrixGroupByOrganisation') !== 'false',
  );

  fromParam$ = queryParam$(this.route, 'selectedFrom').pipe(
    takeUntil(this.destroyed$),
  );
  fromParamDate$ = this.fromParam$.pipe(
    debounceTime(500),
    map((date) => (date ? new Date(date) : undefined)),
  );

  toParam$ = queryParam$(this.route, 'selectedTo').pipe(
    takeUntil(this.destroyed$),
  );
  toParamDate$ = this.toParam$.pipe(
    debounceTime(500),
    map((date) => (date ? new Date(date) : undefined)),
  );

  jobTypesQueryParams$ = queryParam$(this.route, 'jobtypes').pipe(
    map((param): JobTypeEnum[] => {
      if (!param || param === 'ALL') return [];

      return param
        .split(',')
        .map((jobType) => {
          if (jobType === 'ALL') return null;
          return JobTypeEnum[jobType as keyof typeof JobTypeEnum];
        })
        .filter(
          (jobType): jobType is JobTypeEnum =>
            jobType !== null && jobType !== undefined,
        );
    }),
    takeUntil(this.destroyed$),
  );

  shiftsQueryParams$ = queryParam$(this.route, 'shifts').pipe(
    map((param): string[] => {
      if (!param) return [];
      return param
        .split(',')
        .map((shiftName): string | null => {
          // Convert string names to enum values
          switch (shiftName) {
            case 'EARLY':
              return ShiftEnum.EARLY.toString();
            case 'DAY':
              return ShiftEnum.DAY.toString();
            case 'LATE':
              return ShiftEnum.LATE.toString();
            case 'NIGHT':
              return ShiftEnum.NIGHT.toString();
            default:
              return 'ALL';
          }
        })
        .filter((shift): shift is string => shift !== null);
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  showSectionToggle$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer?.enableSectionToggleInJobMatrix),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  // Customer certificates - filtered by customer's visible certificates
  customerCertificates$ = this.certificateService.readyCertificates$.pipe(
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  customerCertificateGroups$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer?.certificateGroups || []),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  certificatesQueryParams$ = queryParam$(this.route, 'certificates').pipe(
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  selectedJobIdsQueryParams$ = queryParam$(this.route, 'selectedJobIds').pipe(
    map((param): string[] => {
      if (!param) return [];
      return param.split(',').filter((id: string) => id.trim() !== '');
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  selectedJobsDateQueryParams$ = queryParam$(
    this.route,
    'selectedJobsDate',
  ).pipe(
    map((param): string | undefined => {
      if (!param) return undefined;
      return param;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  certificatesFilter$: Observable<{
    certificates: MappedCertificate[];
    certificateGroups: CertificateGroup[];
  }> = combineLatest([
    this.certificatesQueryParams$,
    this.customerCertificates$,
    this.customerCertificateGroups$,
  ]).pipe(
    map(
      ([params, certificates, certificateGroups]): {
        certificates: MappedCertificate[];
        certificateGroups: CertificateGroup[];
      } => {
        const param = params;
        const certificateIds = param?.split(',') || [];

        const result: MappedCertificate[] = [];

        // Handle 'ALL' option if present
        if (certificateIds.includes('ALL')) {
          result.push({
            id: 'ALL',
            displayName: 'Alle',
          });
        }
        // Add specific certificates
        const specificCertificates =
          certificates
            ?.filter((certificate) => certificateIds.includes(certificate._id))
            ?.map((certificate) => ({
              id: certificate._id,
              displayName: certificate.name,
            })) || [];

        result.push(...specificCertificates);

        // Add certificate groups
        let selectedGroups = certificateGroups?.filter((group) =>
          certificateIds.includes(group.id),
        );

        return {
          certificates: result,
          certificateGroups: selectedGroups || [],
        };
      },
    ),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  dateRange$ = combineLatest([
    this.fromParamDate$.pipe(filter(Boolean)),
    this.toParamDate$.pipe(filter(Boolean)),
  ]).pipe(
    map(([fromDate, toDate]) => {
      const rangeSize = differenceInDays(toDate, fromDate);
      if (rangeSize <= 0) {
        return undefined;
      }
      if (rangeSize > 90) {
        this.toastService.add({
          message: 'cannotViewMoreThan90Days',
          type: 'INFO',
        });
        return undefined;
      }
      return { fromDate, toDate };
    }),
    shareReplay(1),
  );

  dateRangeDates$: Observable<string[]> = this.dateRange$.pipe(
    filter(Boolean),
    map(({ fromDate, toDate }) => {
      const { defaultFromDate, defaultToDate } = this.getDefaultDateRange();

      return this.generateDateRange(
        fromDate || defaultFromDate,
        toDate || defaultToDate,
      );
    }),
    shareReplay(1),
  );

  getDefaultDateRange(): { defaultFromDate: Date; defaultToDate: Date } {
    return {
      defaultFromDate: subDays(new Date(), 3),
      defaultToDate: addDays(new Date(), 28),
    };
  }

  jobsQuery$: Observable<Mongo.Selector<Job>> = combineLatest([
    this.dateRange$.pipe(filter(Boolean)),
    this.certificatesFilter$,
    this.jobTypesQueryParams$,
    this.shiftsQueryParams$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(
      ([
        { fromDate, toDate },
        { certificates, certificateGroups },
        jobTypes,
        shifts,
        customer,
      ]) => {
        const { defaultFromDate, defaultToDate } = this.getDefaultDateRange();
        const formatedFromDate = format(
          fromDate || defaultFromDate,
          dbDayDateToken,
        );
        const formatedToDate = format(toDate || defaultToDate, dbDayDateToken);

        let filterCounts = 0;

        const query: Mongo.Selector<Job> = {
          date: {
            $gte: formatedFromDate,
            $lte: formatedToDate,
          },
        };

        if (
          formatedFromDate !== format(defaultFromDate, dbDayDateToken) ||
          formatedToDate !== format(defaultToDate, dbDayDateToken)
        ) {
          filterCounts++;
        }

        if (certificates.length > 0 || certificateGroups.length > 0) {
          filterCounts++;
          let queryCertificates = [...certificates];
          if (!certificates.some((c) => c.id === 'ALL')) {
            certificateGroups.forEach((group) => {
              queryCertificates.push(...group.certificates);
            });
            query['certificates.id'] = {
              $in: queryCertificates.map((certificate) => certificate.id),
            };
          }
        }

        if (jobTypes.length > 0) {
          filterCounts++;
          query.jobType = { $in: jobTypes };
        }

        if (shifts.length > 0) {
          filterCounts++;
          if (!shifts.includes('ALL')) {
            query['shifts.shift'] = {
              $in: shifts.map((shift) => parseInt(shift)),
            };
          }
        }

        if (customer?.jobMatrixHideCanceledJobs) {
          query['$or'] = [
            {
              status: { $ne: JobStatusEnum.CANCELED },
            },
            {
              status: JobStatusEnum.CANCELED,
              'cancelReason.id': {
                $in:
                  customer?.jobMatrixCancelReasonExceptions?.map(
                    (exception) => exception.id,
                  ) || [],
              },
            },
          ];
        }

        this.filterCounts = filterCounts;
        return query;
      },
    ),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  jobsAggregationQueryLoading$ = this.jobsQuery$.pipe(
    switchMap(async (query) => {
      this.subscriptionService.subscribeAggregatedJobMatrixJobs(query);
      return query;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  jobsLoading$ = this.subscriptionService.jobMatrixJobsLoading$.pipe(
    rxDelayLoadingState(1000),
    rxNgZoneRun(this.ngZone),
  );

  aggregatedJobs$: Observable<AggregatedJobMatrixJob[]> = combineLatest([
    this.jobsAggregationQueryLoading$,
    this.subscriptionService.aggregatedJobMatrixJobsLoading$,
  ]).pipe(
    switchMap(([query, loading]) => {
      if (loading) {
        return of([] as AggregatedJobMatrixJob[]);
      }

      // Update the query to work for the aggregated jobs collection instead of the jobs collection
      let { ['shifts.shift']: shiftsQuery, ...restOfQuery } = query as any;
      let queryForAggregatedJobs = { ...restOfQuery };
      let shifts = shiftsQuery?.['$in'];

      if (shifts) {
        queryForAggregatedJobs['shift'] = {
          $in: shifts.map((shift: number) => shift.toString()),
        };
      }

      // Use the reactive collection from the publication
      return AggregatedJobMatrixJobs.find(queryForAggregatedJobs, {
        sort: {
          date: 1,
          departmentName: 1,
          shift: 1,
        },
      });
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  organisationMap$: Observable<Map<string, Organisation>> =
    this.subscriptionService.organisations.ready$.pipe(
      switchMap(() => {
        const organisationMap = new Map<string, Organisation>();
        Organisations.find()
          .fetch()
          .forEach((organisation) => {
            organisationMap.set(organisation._id, organisation);
          });
        return of(organisationMap);
      }),
      rxDistinctJSONStringify(),
      shareReplay(1),
      takeUntil(this.destroyed$),
      rxNgZoneRun(this.ngZone),
    );

  /** Rows + totals from one snapshot so counts stay aligned (see normalizeAggregatedJobForMatrix). */
  private matrixProcessed$ = combineLatest([
    this.aggregatedJobs$,
    this.certificatesFilter$,
    this.shiftsQueryParams$,
    this.customerService.readyDispoCustomer$,
    this.organisationMap$,
  ]).pipe(
    debounceTime(300),
    rxNgZoneRun(this.ngZone),
    map(([jobs, certificatesFilter, shifts, customer, rawOrganisationMap]) => {
      const organisationMap = new Map<string, RequestOverviewOrganisationRow>();

      const normalizedJobs = jobs.map((job) =>
        this.normalizeAggregatedJobForMatrix(job, customer),
      );

      normalizedJobs.forEach((job) => {
        // This always gets the current name for the organisation, not the name as it was when the job was created
        const jobOrganisation = rawOrganisationMap.get(job.organisationId);

        let organisationRow = organisationMap.get(job.organisationId);
        if (!organisationRow) {
          organisationMap.set(
            job.organisationId,
            this.createAggregatedOrganisationRow(
              job,
              certificatesFilter,
              shifts,
              jobOrganisation,
            ),
          );
          return;
        }

        organisationRow.jobs = this.fillJobMatrixJobs(
          organisationRow.jobs,
          job,
          certificatesFilter,
          shifts,
        );

        if (job.sectionName) {
          organisationRow =
            organisationRow as RequestOverviewOrganisationRowWithSections;
          if (!organisationRow.sections) {
            organisationRow.sections = [];
          }
          let section = organisationRow.sections.find(
            (section) => section.name === job.sectionName,
          );
          if (!section) {
            organisationRow.sections.push(
              this.createAggregatedSection(job, certificatesFilter, shifts),
            );
          } else {
            section.jobs = this.fillJobMatrixJobs(
              section.jobs,
              job,
              certificatesFilter,
              shifts,
            );
            const department = section.departments.find(
              (department) => department.id === job.departmentId,
            );
            if (!department) {
              section.departments.push(
                this.createAggregatedDepartment(
                  job,
                  certificatesFilter,
                  shifts,
                ),
              );
            } else {
              department.jobs = this.fillJobMatrixJobs(
                department.jobs,
                job,
                certificatesFilter,
                shifts,
              );
            }
          }
        } else {
          if (!organisationRow.departments) {
            organisationRow.departments = [];
          }
          let department = organisationRow.departments.find(
            (department) => department.id === job.departmentId,
          );
          if (!department) {
            organisationRow.departments.push(
              this.createAggregatedDepartment(job, certificatesFilter, shifts),
            );
          } else {
            department.jobs = this.fillJobMatrixJobs(
              department.jobs,
              job,
              certificatesFilter,
              shifts,
            );
          }
        }
      });

      let totalJobsRow: JobMatrixJobs = {
        maxJobs: 0,
        byCertificates: new Map<string, JobMatrixJobsByCertificates>(),
      };
      normalizedJobs.forEach((job) => {
        totalJobsRow = this.fillJobMatrixJobs(
          totalJobsRow,
          job,
          certificatesFilter,
          shifts,
        );
      });

      return {
        organisationMap,
        sectionGroups: this.extractSectionGroups(organisationMap),
        totalJobsRow,
      };
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  processedAggregatedJobs$: Observable<{
    organisationMap: Map<string, RequestOverviewOrganisationRow>;
    sectionGroups: SectionDepartmentGroup[];
  }> = this.matrixProcessed$.pipe(
    map(({ organisationMap, sectionGroups }) => ({
      organisationMap,
      sectionGroups,
    })),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  certificateRows$ = this.certificatesFilter$.pipe(
    map((certificatesFilter) => {
      let certificateRows: { id: string; name: string }[] = [];

      if (
        (!certificatesFilter.certificates.length &&
          !certificatesFilter.certificateGroups.length) ||
        certificatesFilter.certificates.some(
          (certificate) => certificate.id === 'ALL',
        )
      ) {
        certificateRows.push({
          id: 'ALL',
          name: 'Alle',
        });
      } else {
        certificatesFilter.certificateGroups.forEach((certificateGroup) => {
          certificateRows.push({
            id: certificateGroup.id,
            name: certificateGroup.displayName,
          });
        });

        certificatesFilter.certificates.forEach((certificate) => {
          certificateRows.push({
            id: certificate.id,
            name: certificate.displayName,
          });
        });
      }

      return certificateRows;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  totalJobsRow$: Observable<JobMatrixJobs> = this.matrixProcessed$.pipe(
    map(({ totalJobsRow }) => totalJobsRow),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  // Subscribe to full jobs only when side sheet is open (selectedJobIds has values)
  private jobMatrixFullJobsSubscription$ = this.selectedJobIdsQueryParams$.pipe(
    switchMap((selectedIds) => {
      if (!selectedIds || selectedIds.length === 0) {
        // Unsubscribe when side sheet is closed
        this.subscriptionService.subscribeJobMatrixJobs(undefined);
        return of(true); // Return loading=true to prevent querying when side sheet is closed
      }
      // Subscribe to full jobs when side sheet opens - only filter by selected IDs
      this.subscriptionService.subscribeJobMatrixJobs({
        _id: { $in: selectedIds },
      });
      return this.subscriptionService.jobMatrixJobsLoading$;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  // Loading state for full jobs subscription (used by side sheet)
  jobMatrixFullJobsLoading$ = this.jobMatrixFullJobsSubscription$.pipe(
    rxDelayLoadingState(100),
  );

  jobs$: Observable<Job[]> = combineLatest([
    this.selectedJobIdsQueryParams$,
    this.jobMatrixFullJobsSubscription$,
  ]).pipe(
    switchMap(([selectedIds, loading]) => {
      // Only query jobs when subscription is active (side sheet is open) and not loading
      if (loading || !selectedIds || selectedIds.length === 0) {
        return of([] as Job[]);
      }

      // Only filter by selected IDs
      return Jobs.find(
        {
          _id: { $in: selectedIds },
        },
        {
          sort: {
            createdAt: -1,
          },
        },
      );
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  // Observable to finalize organisation row structure with correct max job counts
  organisationRows$: Observable<RequestOverviewOrganisationRowForTemplate[]> =
    combineLatest([
      this.processedAggregatedJobs$,
      this.groupByOrganisation$,
    ]).pipe(
      map(([{ organisationMap, sectionGroups }, groupByOrganisation]) => {
        if (groupByOrganisation) {
          const organsationRowArray: RequestOverviewOrganisationRow[] =
            Array.from(organisationMap.values()).sort((a, b) =>
              a.organisation.name.localeCompare(b.organisation.name),
            );
          return organsationRowArray.map((organisationRow) => ({
            ...organisationRow,
            sections:
              'sections' in organisationRow ? organisationRow.sections : [],
            departments: organisationRow.departments || [],
          })) as RequestOverviewOrganisationRowForTemplate[];
        }
        return this.buildSectionGroupedRows(sectionGroups);
      }),
      shareReplay(1),
      takeUntil(this.destroyed$),
    );

  // Observable for jobs displayed in the side sheet
  selectedJobs$: Observable<Job[]> = combineLatest([
    this.jobs$,
    this.selectedJobIdsQueryParams$,
  ]).pipe(
    map(([allJobs, selectedIds]: [Job[], string[]]) => {
      if (!selectedIds || selectedIds.length === 0) return [];
      return allJobs.filter((job: Job) => selectedIds.includes(job._id));
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  // Add calendar helper observable
  calendarTableHelper$: Observable<CalendarTableHelper> = combineLatest([
    this.dateRange$.pipe(filter(Boolean)),
  ]).pipe(
    map(([{ fromDate, toDate }]) => {
      const defaultFromDate = fromDate || subDays(new Date(), 3);
      const defaultToDate = toDate || addDays(new Date(), 28);

      return this.createCalendarTableHelper(defaultFromDate, defaultToDate);
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private userService: UserService,
    private customerService: CustomerService,
    private datePipe: DatePipe,
    private certificateService: CertificateService,
    public subscriptionService: SubscriptionService,
    private toastService: ToastService,
  ) {
    this.userService.readyUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        if (user.role === RoleEnum.POOL) {
          this.router.navigate(['/']).then();
          return;
        }
        this.loggedInUser = user;
      });

    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        if (!customer?.enableRequestOverview) {
          this.router.navigate(['/']).then();
        }
      });

    // Set default date range if no query params exist
    const currentParams = this.route.snapshot.queryParams;
    if (!currentParams['selectedFrom'] && !currentParams['selectedTo']) {
      const { defaultFromDate, defaultToDate } = this.getDefaultDateRange();
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            selectedFrom: format(defaultFromDate, dbDayDateToken),
            selectedTo: format(defaultToDate, dbDayDateToken),
          },
          queryParamsHandling: 'merge',
        })
        .then();
    }

    // Initialize side sheet state from query params
    if (currentParams['selectedJobIds']) {
      const jobIds = currentParams['selectedJobIds']
        .split(',')
        .filter((id: string) => id.trim() !== '');
      if (jobIds.length > 0) {
        this.selectedJobIdsSubject$.next(jobIds);
        this.sideSheetOpen = true;
      }
    }
  }

  ngOnInit(): void {
    // Subscribe to query params changes to update side sheet state
    this.selectedJobIdsQueryParams$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((jobIds) => {
        if (jobIds.length > 0) {
          this.selectedJobIdsSubject$.next(jobIds);
          this.sideSheetOpen = true;
        } else if (this.sideSheetOpen) {
          this.sideSheetOpen = false;
          this.selectedJobIdsSubject$.next([]);
        }
      });
  }

  updateQueryParams(queryParams: { [key: string]: string | null }): void {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  // New input handling methods for better control
  onFromDateInput(value?: Date): void {
    if (!value) {
      return;
    }
    // this is a workaraunt because the mat-date input falls back to year 2000
    // when the user types 20 (for 20.05.2025) for example
    const oneYearAgo = subYears(new Date(), 1);
    const inTwoYears = addYears(new Date(), 2);
    if (isBefore(value, oneYearAgo) || isAfter(value, inTwoYears)) {
      return;
    }
    this.updateQueryParams({
      selectedFrom: format(value, dbDayDateToken),
    });
  }

  onToDateInput(value?: Date): void {
    if (!value) {
      return;
    }
    // this is a workaraunt because the mat-date input falls back to year 2000
    // when the user types 20 (for 20.05.2025) for example
    const oneYearAgo = subYears(new Date(), 1);
    const inTwoYears = addYears(new Date(), 2);
    if (isBefore(value, oneYearAgo) || isAfter(value, inTwoYears)) {
      return;
    }
    this.updateQueryParams({
      selectedTo: format(value, dbDayDateToken),
    });
  }

  clearDates(): void {
    this.updateQueryParams({
      selectedFrom: null,
      selectedTo: null,
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // Filter toggle method
  toggleFilters(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
    localStorage.setItem(
      'jobMatrixFiltersCollapsed',
      this.filtersCollapsed.toString(),
    );
  }

  toggleGroupByOrganisation(): void {
    const newValue = !this.groupByOrganisation$.getValue();
    this.groupByOrganisation$.next(newValue);
    localStorage.setItem('jobMatrixGroupByOrganisation', newValue.toString());
  }

  // Save current filter settings as default
  saveDefaultFilter(): void {
    if (!this.loggedInUser) return;

    Meteor.call('saveJobMatrixFilter', this.route.snapshot.queryParams);

    this.toastService.add({
      type: 'SUCCESS',
      message: 'savedDefaultFilter',
      timeout: 5000,
    });
  }

  // Clear all filters and reset to defaults
  clearAllFilters(): void {
    const { defaultFromDate, defaultToDate } = this.getDefaultDateRange();
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          selectedFrom: format(defaultFromDate, dbDayDateToken),
          selectedTo: format(defaultToDate, dbDayDateToken),
        },
        replaceUrl: true,
      })
      .then();
  }

  generateDateRange(startDate: Date, endDate: Date): string[] {
    const dates = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }

  trackByJobId(index: number, job: Job): string {
    return job._id;
  }

  // Get effective shifts for the current selection
  effectiveShifts$: Observable<Array<{ shift: string; displayName: string }>> =
    this.shiftsQueryParams$.pipe(
      map((shifts) => {
        // If no shifts selected, use 'ALL'
        let effectiveShifts: string[];
        if (shifts.length === 0) {
          effectiveShifts = ['ALL'];
        } else {
          // Include both 'ALL' and specific shifts if 'ALL' is selected alongside others
          effectiveShifts =
            shifts.includes('ALL') && shifts.length > 1
              ? shifts // Keep all selections including 'ALL'
              : shifts.includes('ALL')
              ? ['ALL'] // Only 'ALL' is selected
              : shifts; // Specific shifts only
        }

        return effectiveShifts.map((shift) => ({
          shift,
          displayName:
            shift === 'ALL'
              ? this.i18n.all || 'Alle'
              : this.i18n.shifts?.[shift] || shift,
        }));
      }),
    );

  /** Immutable prep so minimongo docs are never mutated (multiple subscribers / totals vs rows). */
  private normalizeAggregatedJobForMatrix(
    job: AggregatedJobMatrixJob,
    customer: Customer | undefined,
  ): AggregatedJobMatrixJob {
    let totalJobs = job.totalJobs;
    if (totalJobs < job.bookedJobs) {
      totalJobs = job.bookedJobs;
    }

    let certificates = job.certificates;
    if (
      customer?.jobMatrixCertificatePriority &&
      job.certificates?.some((certificate) =>
        customer.jobMatrixCertificatePriority?.some((priority) =>
          priority.certificates.some((c) => c.id === certificate.id),
        ),
      )
    ) {
      let highestPriority: number | undefined;
      job.certificates.forEach((certificate) => {
        let priority = customer.jobMatrixCertificatePriority?.find((p) =>
          p.certificates.some((c) => c.id === certificate.id),
        );
        if (
          priority &&
          (!highestPriority || priority.priority > highestPriority)
        ) {
          highestPriority = priority.priority;
        }
      });
      const highestPriorityCertificates =
        customer.jobMatrixCertificatePriority?.find(
          (p) => p.priority === highestPriority,
        )?.certificates;
      certificates = job.certificates.filter((certificate) =>
        highestPriorityCertificates?.some((c) => c.id === certificate.id),
      );
    }

    if (totalJobs === job.totalJobs && certificates === job.certificates) {
      return job;
    }
    return { ...job, totalJobs, certificates };
  }

  private extractSectionGroups(
    organisationMap: Map<string, RequestOverviewOrganisationRow>,
  ): SectionDepartmentGroup[] {
    const groups: SectionDepartmentGroup[] = [];
    const emptySectionKey = '-'; // section label for orgs/jobs without sections

    for (const orgRow of organisationMap.values()) {
      const { organisation } = orgRow;

      if ('sections' in orgRow && orgRow.sections) {
        for (const section of orgRow.sections) {
          const sectionName = section.name || emptySectionKey;
          for (const dept of section.departments) {
            groups.push({
              sectionName,
              organisationId: organisation.id,
              organisationName: organisation.name,
              departmentId: dept.id,
              departmentName: dept.name,
              jobs: dept.jobs,
            });
          }
        }
      }
      if (orgRow.departments?.length) {
        for (const dept of orgRow.departments) {
          groups.push({
            sectionName: emptySectionKey,
            organisationId: organisation.id,
            organisationName: organisation.name,
            departmentId: dept.id,
            departmentName: dept.name,
            jobs: dept.jobs,
          });
        }
      }
    }
    return groups;
  }

  private buildSectionGroupedRows(
    sectionGroups: SectionDepartmentGroup[],
  ): RequestOverviewOrganisationRowForTemplate[] {
    const sectionMap = new Map<
      string,
      {
        name: string;
        jobs: JobMatrixJobs;
        departments: RequestOverviewDepartment[];
        orgIds: Set<string>;
      }
    >();

    for (const group of sectionGroups) {
      const sectionName = group.sectionName || '-';
      let targetSection = sectionMap.get(sectionName);
      if (!targetSection) {
        targetSection = {
          name: sectionName,
          jobs: {
            maxJobs: 0,
            byCertificates: new Map<string, JobMatrixJobsByCertificates>(),
          },
          departments: [],
          orgIds: new Set(),
        };
        sectionMap.set(sectionName, targetSection);
      }
      targetSection.orgIds.add(group.organisationId);

      targetSection.jobs = this.mergeJobMatrixJobs(
        targetSection.jobs,
        group.jobs,
      );

      const compositeId = `${group.organisationId}-${group.departmentId}`;
      const displayName =
        targetSection.orgIds.size > 1
          ? `${group.departmentName} (${group.organisationName})`
          : group.departmentName;

      const existingDept = targetSection.departments.find(
        (d) => d.id === compositeId,
      );
      if (!existingDept) {
        targetSection.departments.push({
          id: compositeId,
          name: displayName,
          jobs: group.jobs,
        });
      } else {
        existingDept.jobs = this.mergeJobMatrixJobs(
          existingDept.jobs,
          group.jobs,
        );
      }
    }

    return Array.from(sectionMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((section) => ({
        detailsCollapsed: false,
        organisation: {
          id: section.name,
          name: section.name,
        },
        jobs: section.jobs,
        sections: [],
        departments: section.departments,
      })) as RequestOverviewOrganisationRowForTemplate[];
  }

  private mergeJobMatrixJobs(
    target: JobMatrixJobs,
    source: JobMatrixJobs,
  ): JobMatrixJobs {
    const result: JobMatrixJobs = {
      maxJobs: 0,
      byCertificates: new Map(),
    };
    target.byCertificates.forEach((v, k) =>
      result.byCertificates.set(k, this.deepCloneJobMatrixCertData(v)),
    );
    source.byCertificates.forEach((certData, certId) => {
      const targetCert = result.byCertificates.get(certId);
      if (!targetCert) {
        result.byCertificates.set(
          certId,
          this.deepCloneJobMatrixCertData(certData),
        );
      } else {
        certData.byDate.forEach((dateData, date) => {
          const targetDate = targetCert.byDate.get(date);
          if (!targetDate) {
            targetCert.byDate.set(date, this.deepCloneShiftData(dateData));
          } else {
            dateData.byShift.forEach((counts, shift) => {
              const existing = targetDate.byShift.get(shift);
              if (existing) {
                existing.open += counts.open;
                existing.closed += counts.closed;
                existing.jobIds.push(...counts.jobIds);
              } else {
                targetDate.byShift.set(shift, { ...counts });
              }
            });
          }
        });
      }
    });
    result.maxJobs = this.computeMaxJobsFromByCertificates(result);
    return result;
  }

  computeMaxJobsFromByCertificates(jobs: JobMatrixJobs): number {
    let max = 0;
    jobs.byCertificates.forEach((certData) => {
      certData.byDate.forEach((dateData) => {
        let dateTotal = 0;
        dateData.byShift.forEach((shiftData) => {
          dateTotal += shiftData.open + shiftData.closed;
        });
        max = Math.max(max, dateTotal);
      });
    });
    return max;
  }

  private deepCloneJobMatrixCertData(
    data: JobMatrixJobsByCertificates,
  ): JobMatrixJobsByCertificates {
    const clone: JobMatrixJobsByCertificates = { byDate: new Map() };
    data.byDate.forEach((dateData, date) => {
      const clonedByShift = new Map<
        string,
        { open: number; closed: number; jobIds: string[] }
      >();
      dateData.byShift.forEach((v, k) =>
        clonedByShift.set(k, { ...v, jobIds: [...v.jobIds] }),
      );
      clone.byDate.set(date, { byShift: clonedByShift });
    });
    return clone;
  }

  private deepCloneShiftData(dateData: {
    byShift: Map<string, { open: number; closed: number; jobIds: string[] }>;
  }): {
    byShift: Map<string, { open: number; closed: number; jobIds: string[] }>;
  } {
    const clonedByShift = new Map<
      string,
      { open: number; closed: number; jobIds: string[] }
    >();
    dateData.byShift.forEach((v, k) =>
      clonedByShift.set(k, { ...v, jobIds: [...v.jobIds] }),
    );
    return { byShift: clonedByShift };
  }

  createAggregatedOrganisationRow(
    job: AggregatedJobMatrixJob,
    certificatesFilter: {
      certificates: MappedCertificate[];
      certificateGroups: CertificateGroup[];
    },
    shifts: string[],
    organisation?: Organisation,
  ): RequestOverviewOrganisationRow {
    let organisationRow: RequestOverviewOrganisationRow = {
      detailsCollapsed: false,
      organisation: {
        id: job.organisationId,
        name: organisation?.name || job.organisationName,
      },
      jobs: this.setJobMatrixJobs(job, certificatesFilter, shifts),
      departments: [],
    };

    if (job.sectionName) {
      let { departments, ...rest } = organisationRow;
      organisationRow = {
        ...rest,
        sections: [],
      };
      organisationRow.sections.push(
        this.createAggregatedSection(job, certificatesFilter, shifts),
      );
    } else {
      organisationRow.departments.push(
        this.createAggregatedDepartment(job, certificatesFilter, shifts),
      );
    }

    return organisationRow;
  }

  createAggregatedSection(
    job: AggregatedJobMatrixJob,
    certificatesFilter: {
      certificates: MappedCertificate[];
      certificateGroups: CertificateGroup[];
    },
    shifts: string[],
  ): RequestOverviewSection {
    let section: RequestOverviewSection = {
      detailsCollapsed: false,
      name: job.sectionName || '',
      jobs: this.setJobMatrixJobs(job, certificatesFilter, shifts),
      departments: [
        this.createAggregatedDepartment(job, certificatesFilter, shifts),
      ],
    };

    return section;
  }

  createAggregatedDepartment(
    job: AggregatedJobMatrixJob,
    certificatesFilter: {
      certificates: MappedCertificate[];
      certificateGroups: CertificateGroup[];
    },
    shifts: string[],
  ): RequestOverviewDepartment {
    let department: RequestOverviewDepartment = {
      id: job.departmentId,
      name: job.departmentName,
      jobs: this.setJobMatrixJobs(job, certificatesFilter, shifts),
    };
    return department;
  }

  setJobMatrixJobs(
    job: AggregatedJobMatrixJob,
    certificatesFilter: {
      certificates: MappedCertificate[];
      certificateGroups: CertificateGroup[];
    },
    shifts: string[],
  ): JobMatrixJobs {
    let jobs: JobMatrixJobs = {
      maxJobs: job.totalJobs,
      byCertificates: new Map(),
    };

    if (
      (!certificatesFilter.certificates.length &&
        !certificatesFilter.certificateGroups.length) ||
      certificatesFilter.certificates.some((c) => c.id === 'ALL')
    ) {
      jobs.byCertificates.set('ALL', {
        byDate: new Map([
          [
            job.date,
            {
              byShift: this.getShiftsMap(job, shifts),
            },
          ],
        ]),
      });
    } else {
      certificatesFilter.certificates.forEach((certificate) => {
        if (job.certificates.some((c) => c.id === certificate.id)) {
          jobs.byCertificates.set(certificate.id, {
            byDate: new Map([
              [
                job.date,
                {
                  byShift: this.getShiftsMap(job, shifts),
                },
              ],
            ]),
          });
        }
      });

      certificatesFilter.certificateGroups.forEach((certificateGroup) => {
        if (
          job.certificates.some((c) =>
            certificateGroup.certificates.some((cg) => cg.id === c.id),
          )
        ) {
          jobs.byCertificates.set(certificateGroup.id, {
            byDate: new Map([
              [
                job.date,
                {
                  byShift: this.getShiftsMap(job, shifts),
                },
              ],
            ]),
          });
        }
      });
    }
    return jobs;
  }

  fillJobMatrixJobs(
    jobs: JobMatrixJobs,
    job: AggregatedJobMatrixJob,
    certificatesFilter: {
      certificates: MappedCertificate[];
      certificateGroups: CertificateGroup[];
    },
    shifts: string[],
  ): JobMatrixJobs {
    if (
      (!certificatesFilter.certificates.length &&
        !certificatesFilter.certificateGroups.length) ||
      certificatesFilter.certificates.some((c) => c.id === 'ALL')
    ) {
      jobs.byCertificates = this.fillCertificatesMap(
        jobs.byCertificates,
        job,
        'ALL',
        shifts,
      );
      let maxJobs = this.getMaxJobs(jobs.byCertificates, job, 'ALL', shifts);

      if (jobs.maxJobs < maxJobs) {
        jobs.maxJobs = maxJobs;
      }
    } else {
      certificatesFilter.certificates.forEach((certificate) => {
        if (job.certificates.some((c) => c.id === certificate.id)) {
          jobs.byCertificates = this.fillCertificatesMap(
            jobs.byCertificates,
            job,
            certificate.id,
            shifts,
          );

          let maxJobs = this.getMaxJobs(
            jobs.byCertificates,
            job,
            certificate.id,
            shifts,
          );
          if (jobs.maxJobs < maxJobs) {
            jobs.maxJobs = maxJobs;
          }
        }
      });

      certificatesFilter.certificateGroups.forEach((certificateGroup) => {
        if (
          job.certificates.some((c) =>
            certificateGroup.certificates.some((cg) => cg.id === c.id),
          )
        ) {
          jobs.byCertificates = this.fillCertificatesMap(
            jobs.byCertificates,
            job,
            certificateGroup.id,
            shifts,
          );

          let maxJobs = this.getMaxJobs(
            jobs.byCertificates,
            job,
            certificateGroup.id,
            shifts,
          );
          if (jobs.maxJobs < maxJobs) {
            jobs.maxJobs = maxJobs;
          }
        }
      });
    }

    return jobs;
  }

  fillCertificatesMap(
    byCertificates: Map<string, JobMatrixJobsByCertificates>,
    job: AggregatedJobMatrixJob,
    certificateId: string,
    shifts: string[],
  ): Map<string, JobMatrixJobsByCertificates> {
    if (!byCertificates.get(certificateId)) {
      byCertificates.set(certificateId, {
        byDate: new Map([
          [
            job.date,
            {
              byShift: this.getShiftsMap(job, shifts),
            },
          ],
        ]),
      });
    } else if (!byCertificates.get(certificateId)!.byDate.get(job.date)) {
      byCertificates.get(certificateId)!.byDate.set(job.date, {
        byShift: this.getShiftsMap(job, shifts),
      });
    } else if (
      ((!shifts.length || shifts.includes('ALL')) &&
        !byCertificates
          .get(certificateId)!
          .byDate.get(job.date)
          ?.byShift.get('ALL')) ||
      (shifts.includes(job.shift) &&
        !byCertificates
          .get(certificateId)!
          .byDate.get(job.date)
          ?.byShift.get(job.shift))
    ) {
      let newShiftsMap = this.getShiftsMap(job, shifts);
      byCertificates
        .get(certificateId)!
        .byDate.get(job.date)!
        .byShift.set(job.shift, newShiftsMap.get(job.shift)!);
    } else {
      if (!shifts.length || shifts.includes('ALL')) {
        let shiftData = byCertificates
          .get(certificateId)!
          .byDate.get(job.date)!
          .byShift.get('ALL');
        if (shiftData) {
          shiftData.open += job.totalJobs - job.bookedJobs;
          shiftData.closed += job.bookedJobs;
          shiftData.jobIds.push(...job.jobIds);
        }
      } else if (shifts.includes(job.shift)) {
        let shiftData = byCertificates
          .get(certificateId)!
          .byDate.get(job.date)!
          .byShift.get(job.shift);
        if (shiftData) {
          shiftData.open += job.totalJobs - job.bookedJobs;
          shiftData.closed += job.bookedJobs;
          shiftData.jobIds.push(...job.jobIds);
        }
      }
    }
    return byCertificates;
  }

  getMaxJobs(
    byCertificates: Map<string, JobMatrixJobsByCertificates>,
    job: AggregatedJobMatrixJob,
    certificateId: string,
    shifts: string[],
  ): number {
    let maxJobs = 0;

    if (!shifts.length || shifts.includes('ALL')) {
      let shiftData = byCertificates
        ?.get(certificateId)
        ?.byDate.get(job.date)
        ?.byShift.get('ALL');
      if (shiftData) {
        maxJobs = shiftData.open + shiftData.closed;
      }
    } else if (shifts.includes(job.shift)) {
      let shiftData = byCertificates
        ?.get(certificateId)
        ?.byDate.get(job.date)
        ?.byShift.get(job.shift);
      if (shiftData) {
        maxJobs = shiftData.open + shiftData.closed;
      }
    }

    return maxJobs;
  }

  getShiftsMap(
    job: AggregatedJobMatrixJob,
    shifts: string[],
  ): Map<string, { open: number; closed: number; jobIds: string[] }> {
    let shiftsMap = new Map();
    if (!shifts.length || shifts.includes('ALL')) {
      shiftsMap.set('ALL', {
        open: job.totalJobs - job.bookedJobs,
        closed: job.bookedJobs,
        jobIds: [...job.jobIds],
      });
    } else if (shifts.includes(job.shift)) {
      shiftsMap.set(job.shift, {
        open: job.totalJobs - job.bookedJobs,
        closed: job.bookedJobs,
        jobIds: [...job.jobIds],
      });
    }
    return shiftsMap;
  }

  // Side sheet methods
  openJobDetailsSideSheet(jobIds: string[], date?: string): void {
    this.selectedJobIdsSubject$.next(jobIds);
    this.sideSheetOpen = true;
    this.updateQueryParams({
      selectedJobIds: jobIds.length > 0 ? jobIds.join(',') : null,
      selectedJobsDate: date || null,
    });
  }

  closeSideSheet(): void {
    this.sideSheetOpen = false;
    this.selectedJobIdsSubject$.next([]);
    this.sideSheetTitle = '';
    this.updateQueryParams({
      selectedJobIds: null,
      selectedJobsDate: null,
    });
  }

  private createCalendarTableHelper(
    startDate: Date,
    endDate: Date,
  ): CalendarTableHelper {
    const days: Date[] = [];
    const calendarDays: CalendarDay[] = [];
    const months: CalendarMonth[] = [];
    const weeks: CalendarWeek[] = [];

    // Generate all dates in range
    const currentDate = new Date(startDate);
    let totalIndex = 0;

    while (currentDate <= endDate) {
      const date = new Date(currentDate);
      days.push(date);
      calendarDays.push({
        date,
        totalIndex: totalIndex++,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Group by months
    const monthsMap = new Map<string, CalendarDay[]>();
    calendarDays.forEach((day) => {
      const monthKey = format(day.date, 'yyyy-MM');
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, []);
      }
      monthsMap.get(monthKey)!.push(day);
    });

    monthsMap.forEach((monthDays, monthKey) => {
      months.push({
        name: format(monthDays[0].date, 'MMMM yyyy'),
        days: monthDays,
      });
    });

    // Group by weeks (Monday as first day of week)
    const weeksMap = new Map<string, CalendarDay[]>();
    calendarDays.forEach((day) => {
      const weekNumber = getWeek(day.date, { weekStartsOn: 1 }); // 1 = Monday
      const year = format(day.date, 'yyyy');
      const weekKey = `${year}-${weekNumber}`;
      if (!weeksMap.has(weekKey)) {
        weeksMap.set(weekKey, []);
      }
      weeksMap.get(weekKey)!.push(day);
    });

    weeksMap.forEach((weekDays, weekKey) => {
      const weekNumber = getWeek(weekDays[0].date, { weekStartsOn: 1 }); // 1 = Monday
      weeks.push({
        weekNumber: weekNumber.toString(),
        days: weekDays,
      });
    });

    return {
      start: startDate,
      end: endDate,
      days,
      months,
      weeks,
    };
  }

  // Helper method to get today's date in the correct format
  getToday(): string {
    return format(new Date(), 'yyyy.MM.dd');
  }
}
